import React, { useState } from 'react'
import i18n from 'i18next'
import { css, ClassNames } from '@emotion/react'
import { Body } from './typography/Typography'
import {
    Dialog,
    DialogActions,
    IconButton,
    NewDialogContent,
    NewDialogTitle,
    AddActionEditActionButton
} from './StyledComponents'
import { makeStyles } from '@material-ui/core'
import { AtlasIcon } from 'web-components/atlas-icon'
import { AtlasButtonCss } from 'components/minutetaker/components/ActionItemEditDialog/ActionItemEditDialog'
import {
    ColorBaseDarkBlack,
    ColorBaseRichBlack,
    ColorBaseWhite,
    ColorEastBay,
    FontWeightRegular
} from 'assets/styles/variables'

const useStyles = makeStyles({
    dialog: {
        width: '440px',
        height: '244px'
    }
})

type ConfirmationDialogGenericProps = {
    title: string
    message: string
    confirmLabel: string
    cancelLabel?: string
    onConfirm: () => void
    onCancel: () => void
    confirmAnalytics?: string
    cancelAnalytics?: string
    closeAnalytics?: string
}

const ConfirmationDialogGeneric: React.FC<ConfirmationDialogGenericProps> = ({
    title,
    message,
    confirmLabel,
    cancelLabel,
    onConfirm,
    onCancel,
    confirmAnalytics,
    cancelAnalytics,
    closeAnalytics
}) => {
    const classes = useStyles()
    const [isOpen, setIsOpen] = useState(true)

    const handleCancel = () => {
        if (onCancel) {
            onCancel()
        }
        setIsOpen(false)
    }

    const handleConfirm = () => {
        if (onConfirm) {
            onConfirm()
        }
        setIsOpen(false)
    }

    const effectiveCancelLabel = cancelLabel || i18n.t('CANCEL')

    const dialogActions = [
        <AddActionEditActionButton
            disableFocusRipple
            disableRipple
            key="cancel-btn"
            onClick={handleCancel}
            color="primary"
            data-analytics={cancelAnalytics}>
            {effectiveCancelLabel}
        </AddActionEditActionButton>,
        <ClassNames key="confirm-btn-wrapper">
            {({ css: emotionCss }) => (
                <AddActionEditActionButton
                    className={emotionCss`
                        ${ConfirmButtonCss}
                    `}
                    disableFocusRipple
                    disableRipple
                    key="confirm-btn"
                    onClick={handleConfirm}
                    color="primary"
                    data-analytics={confirmAnalytics}>
                    {confirmLabel}
                </AddActionEditActionButton>
            )}
        </ClassNames>
    ]

    return (
        <ClassNames>
            {({ css: emotionCss }) => (
                <Dialog
                    classes={{ paper: classes.dialog }}
                    maxWidth="sm"
                    className={emotionCss`
                        .MuiDialog-paperWidthSm {
                            max-width: 440px;
                        }
                    `}
                    open={isOpen}
                    scroll="paper"
                    onClose={handleCancel}>
                    <NewDialogTitle
                        className={emotionCss`
                            font-family: Source Sans Pro;
                            font-size: 22px;
                            font-weight: 600;
                        `}>
                        {title}
                        <IconButton
                            tabIndex={0}
                            disableFocusRipple
                            disableRipple
                            onClick={handleCancel}
                            data-analytics={closeAnalytics}
                            className={emotionCss`
                                ${AtlasButtonCss}
                            `}>
                            <AtlasIcon
                                name="close"
                                size={24}
                                data-testid="ConfirmationDialogGeneric_CloseIcon"
                            />
                        </IconButton>
                    </NewDialogTitle>

                    <NewDialogContent>
                        <div
                            className={emotionCss`
                                ${dialogBodyCss}
                            `}>
                            <Body
                                className={emotionCss`
                                    font-family: 'Source Sans Pro';
                                    color: ${ColorBaseDarkBlack};
                                    font-size: 16px;
                                `}
                                type="body1">
                                {message}
                            </Body>
                        </div>
                    </NewDialogContent>

                    <DialogActions>{dialogActions}</DialogActions>
                </Dialog>
            )}
        </ClassNames>
    )
}

export default ConfirmationDialogGeneric

const dialogBodyCss = css`
    color: ${ColorBaseRichBlack};
    display: flex;
    margin: 0 auto;
    margin-top: 20px;
    width: 100%;
    span {
        font-family: 'Source Sans Pro';
        font-weight: ${FontWeightRegular};
        font-size: 16px;
        line-height: 20px;
    }
`

const ConfirmButtonCss = css`
    background-color: ${ColorEastBay};
    border-radius: 2px;
    color: ${ColorBaseWhite};
    &:hover {
        background-color: #364262;
    }
`
